import { useCallback, useEffect, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { SectionCard } from "@/components/common/SectionCard";
import { usePagination } from "@/components/common/usePagination";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  listManufacturingLaborPayMatches,
  type LaborPayMatchLine,
  type LaborPayMatchUnit,
} from "@/lib/manufacturing/laborPayListClient";
import { LABOR_PAY_DEFAULT_PAGE_SIZE, LABOR_PAY_PAGE_SIZES } from "@/lib/manufacturing/laborPayPaging";
import { isLaborLineOpen } from "@/lib/manufacturing/laborCompanyPayMatch";
import { draftFromUnmatchedLine, type CompanyRateDraft } from "./useManufacturingLaborCompanyRates";

function money(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function warnLabel(line: LaborPayMatchLine): string {
  const bits: string[] = [];
  if (line.labor === "unmatched") bits.push("no labor rate");
  if (line.labor === "needs_pays_by") bits.push("needs Pays by");
  if (line.labor === "needs_size") bits.push("need building size");
  if (line.model === "unmatched") bits.push("not in models");
  return bits.join(" · ");
}

export function ManufacturingLaborPayMatchInbox({
  plantId,
  navTick,
  onAddRate,
  readOnly = false,
  search = "",
  embedded = false,
}: {
  plantId: string;
  navTick: number;
  onAddRate?: (draft: CompanyRateDraft) => void;
  readOnly?: boolean;
  search?: string;
  embedded?: boolean;
}) {
  const [units, setUnits] = useState<LaborPayMatchUnit[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!plantId) {
      setUnits([]);
      return;
    }
    setBusy(true);
    setError(null);
    const res = await listManufacturingLaborPayMatches(plantId);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setUnits(res.data.units);
  }, [plantId]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  const unmatched = units.filter((u) => {
    if (u.unmatched_labor_count === 0 && u.unmatched_model_count === 0) return false;
    if (!search) return true;
    const blob = [
      u.serial,
      u.recipe_label,
      u.phase_name,
      ...u.lines.map((l) => `${l.line.label} ${l.line.option_key} ${l.line.key}`),
    ]
      .join(" ")
      .toLowerCase();
    return blob.includes(search);
  });
  const page = usePagination(unmatched, LABOR_PAY_DEFAULT_PAGE_SIZE);

  const body = (
    <>
      {error ? <p className="mb-2 text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {busy && unmatched.length === 0 ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : unmatched.length === 0 ? (
        <p className="text-sm text-gray-500">No unmatched lines on this plant’s board.</p>
      ) : (
        <ul className="space-y-3">
          {page.paginatedItems.map((unit) => (
            <li
              key={unit.unit_id}
              className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 dark:border-amber-900 dark:bg-amber-950/30"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="font-mono text-sm text-gray-900 dark:text-zinc-100">{unit.serial}</div>
                <div className="text-xs text-gray-600 dark:text-zinc-400">
                  {unit.recipe_label} · {unit.phase_name} · invoice {money(unit.payout_usd)}
                </div>
              </div>
              <ul className="mt-2 space-y-1">
                {unit.lines
                  .filter((line) => isLaborLineOpen(line.labor) || line.model === "unmatched")
                  .map((line) => (
                    <li
                      key={`${unit.unit_id}-${line.line.key}`}
                      className="flex flex-wrap items-center justify-between gap-2 text-sm"
                    >
                      <span>
                        {line.line.label} × {line.line.quantity}
                        <span className="ml-2 text-xs font-medium text-amber-800 dark:text-amber-200">
                          {warnLabel(line)}
                        </span>
                      </span>
                      {line.labor === "unmatched" && onAddRate && !readOnly ? (
                        <button
                          type="button"
                          className={ghostBtnClass}
                          onClick={() => onAddRate?.(draftFromUnmatchedLine(line.line))}
                        >
                          Add labor rate
                        </button>
                      ) : null}
                    </li>
                  ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      <PaginationBar
        pageIndex={page.pageIndex}
        pageCount={page.pageCount}
        pageSize={page.pageSize}
        totalCount={page.totalCount}
        firstShown={page.firstShown}
        lastShown={page.lastShown}
        onPageChange={page.setPageIndex}
        onPageSizeChange={page.setPageSize}
        pageSizeOptions={LABOR_PAY_PAGE_SIZES}
        noun="orders"
        alwaysShow
      />
    </>
  );

  if (embedded) return body;

  return (
    <SectionCard
      title="Unmatched order lines"
      description={
        readOnly
          ? "Orders whose options do not match a model or company labor rate. Ops adds missing $ × qty rates."
          : "Orders on the board whose options do not match a model recipe/variant or a company labor rate. Add the missing model, or add a $ × qty rate (use $0 to acknowledge a non-billable line)."
      }
    >
      {body}
    </SectionCard>
  );
}
